import axios, { isCancel } from 'axios';
import { getAiUrl } from '../runtime/utils/common';
import { matchesServiceRequest } from '../runtime/utils/httpFailure';
import { preservesPageView, sharesPageContentTransition } from '../components/ideo/ui-content-switch/pageContentTransition';
import { settleAgentEntityRequest, trackAgentEntityRequest } from '#layers/core/app/runtime/utils/agentEntityLoad';

export default defineNuxtPlugin({
    name: 'http',
    dependsOn: ['alerts', 'auth', 'axios', 'i18n'],
    setup()
    {
        const { $alert, $auth } = useNuxtApp();
        const router = useRouter();
        const viewLoading = useViewLoadingStore();
        const requestGenerations = new WeakMap<object, number | null>();

        viewLoading.beginNavigation();
        router.beforeEach((to, from) =>
        {
            if (preservesPageView(to, from) || sharesPageContentTransition(to, from))
                viewLoading.reset();
            else
                viewLoading.beginNavigation();
        });
        router.afterEach(() => viewLoading.finishNavigationWindow());
        void router.isReady().then(() => viewLoading.finishNavigationWindow());

        function finishViewRequest(config: object | undefined)
        {
            if (!config)
                return;

            viewLoading.finishRequest(requestGenerations.get(config) ?? null);
            requestGenerations.delete(config);
        }

        axios.interceptors.request.use((config) =>
        {
            requestGenerations.set(config, viewLoading.startRequest());

            // if ($auth.token())
            //     config.headers.Authorization = `Bearer ${$auth.token()}`;

            if ($auth.impersonating())
                config.headers['Access-Impersonate'] = $auth.impersonatingValue();

            // Tutaj, bo przechwytywacz widzi wynik zapytania, zanim `catch` widoku go połknie
            trackAgentEntityRequest(config);

            return config;
        },
        (error) =>
        {
            finishViewRequest(error.config);

            return Promise.reject(error);
        });
        axios.interceptors.response.use(
            (response) =>
            {
                finishViewRequest(response.config);

                if (response.status === 200 && response.config.responseType !== 'stream' && response.headers['content-type']?.includes('text/'))
                {
                    const rejection = {
                        code: 500,
                        message: 'Unexpected exception occured.',
                        data: null as any,
                        handled: false
                    };

                    settleAgentEntityRequest(response.config, rejection);

                    return Promise.reject(rejection);
                }

                settleAgentEntityRequest(response.config, null);

                return response;
            },
            (error) =>
            {
                finishViewRequest(error.config);

                const ex = {
                    code: 500,
                    message: 'Unexpected exception occured.',
                    data: null as any,
                    inner: error,
                    handled: false
                };

                if (error.response)
                {
                    ex.code = error.response.status;
                    ex.data = (typeof error.response.data === 'string') ? { message: error.response.data } : error.response.data || {};
                    ex.message = 'message' in ex.data ? ex.data.message : error.message;
                }
                else
                {
                    ex.message = error.message;
                }

                const cancelled = isCancel(error);
                const aiRequest = matchesServiceRequest(error.config?.url, getAiUrl());
                const suppressErrorNotification = error.config?.suppressErrorNotification === true && ex.code !== 401;

                if (ex.code >= 400 && !suppressErrorNotification)
                {
                    switch (ex.code)
                    {
                        /* 401 - Unauthorized */
                        case 401:
                        {
                            if (!error.config.url.startsWith('auth/'))
                                $auth.logout();

                            break;
                        }
                        /* 403 - Forbidden */
                        case 403:
                        {
                            setTimeout(() =>
                            {
                                if (!ex.handled)
                                {
                                    const message = $t('[[[Nie masz uprawnień do tego widoku. Stan widoku mógł ulec zmianie, odśwież i sprawdź lub spróbuj ponownie później.]]]');
                                    const duration = 1000 * 3600 * 24;

                                    $alert.warning(message, { dismissible: false, important: true, duration, action: { label: $t('[[[Odśwież]]]'), onClick: () => location.reload() } });
                                }
                            },
                            100);

                            break;
                        }
                        /* 404 - Not Found */
                        case 404:
                        {
                            setTimeout(() =>
                            {
                                if (!ex.handled)
                                {
                                    $alert.debounceError($t('[[[Błąd 404. Nie znaleziono żądanego zasobu.]]]'));
                                }
                            },
                            100);

                            break;
                        }
                        /* 500 - Internal Server Error
                           503 - Service Unavailable */
                        case 500:
                        case 503:
                        {
                            if (cancelled)
                                break;

                            if (aiRequest)
                                $alert.debounceDanger($t('[[[Usługa AI jest obecnie niedostępna. Spróbuj ponownie za chwilę.]]]'));
                            else if (error.response)
                                $alert.debounceDanger($t('[[[Wystąpił błąd serwera podczas obsługiwania wybranej operacji. Spróbuj ponownie.]]]'));
                            else
                                $alert.debounceDanger($t('[[[Wystąpił błąd połączenia. Upewnij się że masz włączony internet i spróbuj ponownie.]]]'));

                            if (ex.data && ex.data.stackTrace)
                            {
                                // eslint-disable-next-line no-console
                                console.log(ex.data.message);
                                // eslint-disable-next-line no-console
                                console.log(ex.data.stackTrace);
                            }

                            break;
                        }
                        default:
                        {
                            if (ex.code > 500)
                                $alert.debounceError($t('[[[Wystąpił nieznany błąd serwera podczas obsługiwania wybranej operacji. Spróbuj ponownie.]]]'));

                            break;
                        }
                    }
                }

                settleAgentEntityRequest(error.config, ex);

                return Promise.reject(ex);
            }
        );
    }
});
